import React, { useState, useEffect } from 'react';
import {
  RefreshCw, Plus, Trash2, Edit2, CheckCircle, XCircle,
  ChevronDown, ChevronRight, User, Calendar, AlertTriangle,
  Package, Clock, X, Save
} from 'lucide-react';
import Modal from './Modal';
import { apiService } from '../services/api';
import { showGlobalNotification } from '../hooks/useNotifications';
import { useAuth } from '../contexts/AuthContext';

interface UpdateItem {
  id: number;
  update_check_id: number;
  application_name: string;
  current_version: string | null;
  target_version: string | null;
  item_status: 'pending' | 'completed' | 'cannot_update' | 'skipped';
  completed_by_id: number | null;
  completed_by_name: string | null;
  completed_at: string | null;
  notes: string | null;
}

interface UpdateCheck {
  id: number;
  server_type: string;
  server_id: number;
  title: string;
  overall_status: 'pending' | 'in_progress' | 'completed' | 'cannot_update';
  assigned_to: number | null;
  assigned_to_name: string | null;
  created_by: number;
  created_by_name: string;
  completed_by: number | null;
  completed_by_name: string | null;
  completed_at: string | null;
  cannot_update_reason: string | null;
  notes: string | null;
  created_at: string;
  items: UpdateItem[];
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  serverType: 'physical' | 'virtual';
  serverId: number;
  serverName: string;
  adminUsers: { id: number; name: string; role: string }[];
}

const statusLabel = (s: string) => {
  switch (s) {
    case 'pending': return 'Bekliyor';
    case 'in_progress': return 'Devam Ediyor';
    case 'completed': return 'Tamamlandı';
    case 'cannot_update': return 'Güncellenemiyor';
    case 'skipped': return 'Atlandı';
    default: return s;
  }
};

const statusColor = (s: string) => {
  switch (s) {
    case 'pending': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
    case 'in_progress': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
    case 'completed': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
    case 'cannot_update': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
    case 'skipped': return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400';
    default: return 'bg-gray-100 text-gray-600';
  }
};

const ServerUpdateCheckModal: React.FC<Props> = ({
  isOpen, onClose, serverType, serverId, serverName, adminUsers
}) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'active' | 'completed'>('active');
  const [checks, setChecks] = useState<UpdateCheck[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedChecks, setExpandedChecks] = useState<Set<number>>(new Set());

  // New check form
  const [showNewCheckForm, setShowNewCheckForm] = useState(false);
  const [newCheckTitle, setNewCheckTitle] = useState('');
  const [newCheckAssignedTo, setNewCheckAssignedTo] = useState<number>(0);
  const [newCheckNotes, setNewCheckNotes] = useState('');

  // Edit check state
  const [editingCheck, setEditingCheck] = useState<UpdateCheck | null>(null);
  const [editCheckTitle, setEditCheckTitle] = useState('');
  const [editCheckAssignedTo, setEditCheckAssignedTo] = useState<number>(0);
  const [editCheckNotes, setEditCheckNotes] = useState('');
  const [editCheckCannotReason, setEditCheckCannotReason] = useState('');

  // Cannot update reason modal
  const [cannotUpdateCheckId, setCannotUpdateCheckId] = useState<number | null>(null);
  const [cannotUpdateReason, setCannotUpdateReason] = useState('');

  // New item form per check
  const [addingItemForCheck, setAddingItemForCheck] = useState<number | null>(null);
  const [newItemApp, setNewItemApp] = useState('');
  const [newItemCurrent, setNewItemCurrent] = useState('');
  const [newItemTarget, setNewItemTarget] = useState('');
  const [newItemNotes, setNewItemNotes] = useState('');

  // Edit item state
  const [editingItem, setEditingItem] = useState<{ checkId: number; item: UpdateItem } | null>(null);
  const [editItemApp, setEditItemApp] = useState('');
  const [editItemCurrent, setEditItemCurrent] = useState('');
  const [editItemTarget, setEditItemTarget] = useState('');
  const [editItemNotes, setEditItemNotes] = useState('');

  // Cannot update item modal
  const [cannotItemState, setCannotItemState] = useState<{ checkId: number; itemId: number } | null>(null);
  const [cannotItemReason, setCannotItemReason] = useState('');

  useEffect(() => {
    if (isOpen) loadChecks();
  }, [isOpen, serverId, serverType]);

  const loadChecks = async () => {
    setLoading(true);
    try {
      const data = await apiService.getServerUpdateChecks(serverType, serverId);
      setChecks(Array.isArray(data) ? data : []);
    } catch {
      showGlobalNotification.error('Hata', 'Güncelleme kontrolleri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const activeChecks = checks.filter(c => c.overall_status !== 'completed');
  const completedChecks = checks.filter(c => c.overall_status === 'completed');

  const toggleExpand = (id: number) => {
    setExpandedChecks(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleCreateCheck = async () => {
    if (!newCheckTitle.trim()) {
      showGlobalNotification.error('Hata', 'Başlık zorunludur');
      return;
    }
    try {
      await apiService.createServerUpdateCheck({
        server_type: serverType,
        server_id: serverId,
        title: newCheckTitle.trim(),
        assigned_to: newCheckAssignedTo || null,
        notes: newCheckNotes || null
      });
      showGlobalNotification.success('Başarılı', 'Güncelleme kontrolü oluşturuldu');
      setNewCheckTitle('');
      setNewCheckAssignedTo(0);
      setNewCheckNotes('');
      setShowNewCheckForm(false);
      await loadChecks();
    } catch {
      showGlobalNotification.error('Hata', 'Güncelleme kontrolü oluşturulamadı');
    }
  };

  const handleDeleteCheck = async (id: number) => {
    if (!confirm('Bu güncelleme kontrolünü silmek istediğinizden emin misiniz?')) return;
    try {
      await apiService.deleteServerUpdateCheck(id);
      showGlobalNotification.success('Başarılı', 'Güncelleme kontrolü silindi');
      await loadChecks();
    } catch {
      showGlobalNotification.error('Hata', 'Silme işlemi başarısız');
    }
  };

  const handleEditCheckOpen = (check: UpdateCheck) => {
    setEditingCheck(check);
    setEditCheckTitle(check.title);
    setEditCheckAssignedTo(check.assigned_to || 0);
    setEditCheckNotes(check.notes || '');
    setEditCheckCannotReason(check.cannot_update_reason || '');
  };

  const handleEditCheckSave = async () => {
    if (!editingCheck) return;
    try {
      await apiService.updateServerUpdateCheck(editingCheck.id, {
        title: editCheckTitle,
        assigned_to: editCheckAssignedTo || null,
        notes: editCheckNotes || null,
        cannot_update_reason: editCheckCannotReason || null
      });
      showGlobalNotification.success('Başarılı', 'Güncelleme kontrolü güncellendi');
      setEditingCheck(null);
      await loadChecks();
    } catch {
      showGlobalNotification.error('Hata', 'Güncelleme başarısız');
    }
  };

  const handleMarkCheckCompleted = async (id: number) => {
    try {
      await apiService.updateServerUpdateCheck(id, { overall_status: 'completed' });
      showGlobalNotification.success('Başarılı', 'Güncelleme kontrolü tamamlandı olarak işaretlendi');
      await loadChecks();
    } catch {
      showGlobalNotification.error('Hata', 'İşlem başarısız');
    }
  };

  const handleMarkCheckCannotUpdate = async () => {
    if (!cannotUpdateCheckId) return;
    try {
      await apiService.updateServerUpdateCheck(cannotUpdateCheckId, {
        overall_status: 'cannot_update',
        cannot_update_reason: cannotUpdateReason || null
      });
      showGlobalNotification.success('Başarılı', 'Güncellenemiyor olarak işaretlendi');
      setCannotUpdateCheckId(null);
      setCannotUpdateReason('');
      await loadChecks();
    } catch {
      showGlobalNotification.error('Hata', 'İşlem başarısız');
    }
  };

  const handleReopenCheck = async (id: number) => {
    try {
      await apiService.updateServerUpdateCheck(id, { overall_status: 'in_progress' });
      showGlobalNotification.success('Başarılı', 'Güncelleme kontrolü yeniden açıldı');
      await loadChecks();
    } catch {
      showGlobalNotification.error('Hata', 'İşlem başarısız');
    }
  };

  const handleAddItem = async (checkId: number) => {
    if (!newItemApp.trim()) {
      showGlobalNotification.error('Hata', 'Uygulama adı zorunludur');
      return;
    }
    try {
      await apiService.addServerUpdateItem(checkId, {
        application_name: newItemApp.trim(),
        current_version: newItemCurrent || null,
        target_version: newItemTarget || null,
        notes: newItemNotes || null
      });
      showGlobalNotification.success('Başarılı', 'Güncelleme kalemi eklendi');
      setAddingItemForCheck(null);
      setNewItemApp(''); setNewItemCurrent(''); setNewItemTarget(''); setNewItemNotes('');
      await loadChecks();
    } catch {
      showGlobalNotification.error('Hata', 'Kalem eklenemedi');
    }
  };

  const handleEditItemOpen = (checkId: number, item: UpdateItem) => {
    setEditingItem({ checkId, item });
    setEditItemApp(item.application_name);
    setEditItemCurrent(item.current_version || '');
    setEditItemTarget(item.target_version || '');
    setEditItemNotes(item.notes || '');
  };

  const handleEditItemSave = async () => {
    if (!editingItem) return;
    try {
      await apiService.updateServerUpdateItem(editingItem.checkId, editingItem.item.id, {
        application_name: editItemApp,
        current_version: editItemCurrent || null,
        target_version: editItemTarget || null,
        notes: editItemNotes || null
      });
      showGlobalNotification.success('Başarılı', 'Güncelleme kalemi güncellendi');
      setEditingItem(null);
      await loadChecks();
    } catch {
      showGlobalNotification.error('Hata', 'Güncelleme başarısız');
    }
  };

  const handleItemStatusChange = async (checkId: number, itemId: number, status: string) => {
    try {
      await apiService.updateServerUpdateItem(checkId, itemId, { item_status: status });
      showGlobalNotification.success('Başarılı', 'Durum güncellendi');
      await loadChecks();
    } catch {
      showGlobalNotification.error('Hata', 'Güncelleme başarısız');
    }
  };

  const handleItemCannotUpdate = async () => {
    if (!cannotItemState) return;
    try {
      await apiService.updateServerUpdateItem(cannotItemState.checkId, cannotItemState.itemId, {
        item_status: 'cannot_update',
        notes: cannotItemReason || null
      });
      showGlobalNotification.success('Başarılı', 'Güncellenemiyor olarak işaretlendi');
      setCannotItemState(null);
      setCannotItemReason('');
      await loadChecks();
    } catch {
      showGlobalNotification.error('Hata', 'İşlem başarısız');
    }
  };

  const handleDeleteItem = async (checkId: number, itemId: number) => {
    if (!confirm('Bu güncelleme kalemini silmek istediğinizden emin misiniz?')) return;
    try {
      await apiService.deleteServerUpdateItem(checkId, itemId);
      showGlobalNotification.success('Başarılı', 'Kalem silindi');
      await loadChecks();
    } catch {
      showGlobalNotification.error('Hata', 'Silme başarısız');
    }
  };

  const renderItem = (check: UpdateCheck, item: UpdateItem) => (
    <div key={item.id} className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm text-gray-900 dark:text-gray-100">{item.application_name}</span>
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(item.item_status)}`}>
            {statusLabel(item.item_status)}
          </span>
        </div>
        <div className="flex items-center gap-4 mt-1 text-xs text-gray-500 dark:text-gray-400 flex-wrap">
          {item.current_version && (
            <span>Mevcut: <span className="font-mono text-gray-700 dark:text-gray-300">{item.current_version}</span></span>
          )}
          {item.target_version && (
            <span>Hedef: <span className="font-mono text-green-700 dark:text-green-400">{item.target_version}</span></span>
          )}
          {item.completed_by_name && (
            <span className="flex items-center gap-1">
              <User className="w-3 h-3" />
              {item.completed_by_name}
              {item.completed_at && ` — ${new Date(item.completed_at).toLocaleDateString('tr-TR')}`}
            </span>
          )}
        </div>
        {item.notes && (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 italic">{item.notes}</p>
        )}
      </div>
      {check.overall_status !== 'completed' && (
        <div className="flex items-center gap-1 shrink-0">
          {item.item_status === 'pending' && (
            <>
              <button
                onClick={() => handleItemStatusChange(check.id, item.id, 'completed')}
                className="p-1 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded"
                title="Tamamlandı olarak işaretle"
              >
                <CheckCircle className="w-4 h-4" />
              </button>
              <button
                onClick={() => { setCannotItemState({ checkId: check.id, itemId: item.id }); setCannotItemReason(item.notes || ''); }}
                className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                title="Güncellenemiyor olarak işaretle"
              >
                <XCircle className="w-4 h-4" />
              </button>
            </>
          )}
          {(item.item_status === 'completed' || item.item_status === 'cannot_update') && (
            <button
              onClick={() => handleItemStatusChange(check.id, item.id, 'pending')}
              className="p-1 text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded"
              title="Bekliyor'a geri al"
            >
              <Clock className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => handleEditItemOpen(check.id, item)}
            className="p-1 text-gray-400 hover:text-blue-600 rounded"
            title="Düzenle"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDeleteItem(check.id, item.id)}
            className="p-1 text-gray-400 hover:text-red-600 rounded"
            title="Sil"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );

  const renderCheck = (check: UpdateCheck, isCompleted = false) => {
    const expanded = expandedChecks.has(check.id);
    const pendingCount = check.items.filter(i => i.item_status === 'pending').length;
    const doneCount = check.items.filter(i => i.item_status === 'completed').length;
    const cannotCount = check.items.filter(i => i.item_status === 'cannot_update').length;

    return (
      <div key={check.id} className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden mb-3">
        {/* Check Header */}
        <div className="flex items-center gap-3 p-4 bg-white dark:bg-gray-800 cursor-pointer" onClick={() => toggleExpand(check.id)}>
          <button className="text-gray-400 shrink-0">
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-gray-900 dark:text-gray-100">{check.title}</span>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColor(check.overall_status)}`}>
                {statusLabel(check.overall_status)}
              </span>
              {check.items.length > 0 && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {doneCount}/{check.items.length} tamamlandı
                  {cannotCount > 0 && `, ${cannotCount} güncellenemiyor`}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 dark:text-gray-400 flex-wrap">
              <span className="flex items-center gap-1">
                <User className="w-3 h-3" />
                Oluşturan: {check.created_by_name}
              </span>
              {check.assigned_to_name && (
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3 text-blue-500" />
                  Atanan: {check.assigned_to_name}
                </span>
              )}
              {check.completed_by_name && (
                <span className="flex items-center gap-1">
                  <CheckCircle className="w-3 h-3 text-green-500" />
                  Tamamlayan: {check.completed_by_name}
                  {check.completed_at && ` — ${new Date(check.completed_at).toLocaleDateString('tr-TR')}`}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {new Date(check.created_at).toLocaleDateString('tr-TR')}
              </span>
            </div>
            {check.overall_status === 'cannot_update' && check.cannot_update_reason && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400 italic">
                Sebep: {check.cannot_update_reason}
              </p>
            )}
          </div>
          {/* Actions */}
          {!isCompleted && (
            <div className="flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
              <button
                onClick={() => handleMarkCheckCompleted(check.id)}
                className="p-1.5 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded"
                title="Tamamlandı olarak işaretle"
              >
                <CheckCircle className="w-4 h-4" />
              </button>
              <button
                onClick={() => { setCannotUpdateCheckId(check.id); setCannotUpdateReason(check.cannot_update_reason || ''); }}
                className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                title="Güncellenemiyor olarak işaretle"
              >
                <XCircle className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleEditCheckOpen(check)}
                className="p-1.5 text-gray-400 hover:text-blue-600 rounded"
                title="Düzenle"
              >
                <Edit2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleDeleteCheck(check.id)}
                className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                title="Sil"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )}
          {isCompleted && (
            <div className="flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
              <button
                onClick={() => handleReopenCheck(check.id)}
                className="p-1.5 text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded text-xs"
                title="Yeniden aç"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleDeleteCheck(check.id)}
                className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                title="Sil"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Expanded Content */}
        {expanded && (
          <div className="border-t border-gray-200 dark:border-gray-600 p-4 bg-gray-50 dark:bg-gray-800/50 space-y-3">
            {check.notes && (
              <p className="text-sm text-gray-600 dark:text-gray-400 italic bg-white dark:bg-gray-700 p-2 rounded border border-gray-200 dark:border-gray-600">
                {check.notes}
              </p>
            )}

            {/* Items */}
            {check.items.length > 0 ? (
              <div className="space-y-2">
                {check.items.map(item => renderItem(check, item))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-2">
                Henüz güncelleme kalemi eklenmemiş
              </p>
            )}

            {/* Add Item Form */}
            {!isCompleted && addingItemForCheck === check.id ? (
              <div className="border border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 space-y-2">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <input
                    type="text"
                    placeholder="Uygulama / Bileşen adı *"
                    value={newItemApp}
                    onChange={e => setNewItemApp(e.target.value)}
                    className="px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                  <input
                    type="text"
                    placeholder="Mevcut sürüm"
                    value={newItemCurrent}
                    onChange={e => setNewItemCurrent(e.target.value)}
                    className="px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono"
                  />
                  <input
                    type="text"
                    placeholder="Hedef sürüm"
                    value={newItemTarget}
                    onChange={e => setNewItemTarget(e.target.value)}
                    className="px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono"
                  />
                </div>
                <input
                  type="text"
                  placeholder="Not (opsiyonel)"
                  value={newItemNotes}
                  onChange={e => setNewItemNotes(e.target.value)}
                  className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => handleAddItem(check.id)}
                    className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                  >
                    <Save className="w-3.5 h-3.5" />
                    Ekle
                  </button>
                  <button
                    onClick={() => { setAddingItemForCheck(null); setNewItemApp(''); setNewItemCurrent(''); setNewItemTarget(''); setNewItemNotes(''); }}
                    className="flex items-center gap-1 px-3 py-1.5 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 text-sm rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <X className="w-3.5 h-3.5" />
                    İptal
                  </button>
                </div>
              </div>
            ) : !isCompleted && (
              <button
                onClick={() => { setAddingItemForCheck(check.id); setNewItemApp(''); setNewItemCurrent(''); setNewItemTarget(''); setNewItemNotes(''); }}
                className="flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                <Plus className="w-3.5 h-3.5" />
                Güncelleme kalemi ekle
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} zIndex="z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-3xl max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="p-5 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
                <RefreshCw className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Güncelleme Kontrolü</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">{serverName}</p>
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 dark:border-gray-700 shrink-0">
            <button
              onClick={() => setActiveTab('active')}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                activeTab === 'active'
                  ? 'text-orange-600 dark:text-orange-400 border-b-2 border-orange-500'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              Aktif Güncellemeler
              {activeChecks.length > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-full text-xs">
                  {activeChecks.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('completed')}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                activeTab === 'completed'
                  ? 'text-green-600 dark:text-green-400 border-b-2 border-green-500'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              Tamamlanan Güncellemeler
              {completedChecks.length > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-xs">
                  {completedChecks.length}
                </span>
              )}
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-5">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
              </div>
            ) : activeTab === 'active' ? (
              <>
                {/* New Check Button */}
                {!showNewCheckForm && (
                  <button
                    onClick={() => setShowNewCheckForm(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm rounded-lg mb-4 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Yeni Güncelleme Kontrolü Oluştur
                  </button>
                )}

                {/* New Check Form */}
                {showNewCheckForm && (
                  <div className="border border-orange-200 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/10 rounded-lg p-4 mb-4 space-y-3">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Yeni Güncelleme Kontrolü</h3>
                    <input
                      type="text"
                      placeholder="Başlık *"
                      value={newCheckTitle}
                      onChange={e => setNewCheckTitle(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                    <select
                      value={newCheckAssignedTo}
                      onChange={e => setNewCheckAssignedTo(parseInt(e.target.value))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      <option value={0}>Atama yapılmadı</option>
                      {adminUsers.map(u => (
                        <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
                      ))}
                    </select>
                    <textarea
                      placeholder="Not (opsiyonel)"
                      value={newCheckNotes}
                      onChange={e => setNewCheckNotes(e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                    <div className="flex gap-2">
                      <button onClick={handleCreateCheck} className="flex items-center gap-1 px-3 py-1.5 bg-orange-500 text-white text-sm rounded-md hover:bg-orange-600">
                        <Save className="w-3.5 h-3.5" /> Oluştur
                      </button>
                      <button onClick={() => { setShowNewCheckForm(false); setNewCheckTitle(''); setNewCheckAssignedTo(0); setNewCheckNotes(''); }} className="flex items-center gap-1 px-3 py-1.5 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 text-sm rounded-md hover:bg-gray-100 dark:hover:bg-gray-700">
                        <X className="w-3.5 h-3.5" /> İptal
                      </button>
                    </div>
                  </div>
                )}

                {activeChecks.length === 0 ? (
                  <div className="text-center py-12">
                    <Package className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600 mb-3" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">Aktif güncelleme kontrolü bulunmuyor</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Yeni bir güncelleme kontrolü oluşturun</p>
                  </div>
                ) : (
                  activeChecks.map(check => renderCheck(check, false))
                )}
              </>
            ) : (
              <>
                {completedChecks.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600 mb-3" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">Henüz tamamlanan güncelleme bulunmuyor</p>
                  </div>
                ) : (
                  completedChecks.map(check => renderCheck(check, true))
                )}
              </>
            )}
          </div>
        </div>
      </Modal>

      {/* Cannot Update Check Modal */}
      <Modal isOpen={cannotUpdateCheckId !== null} onClose={() => { setCannotUpdateCheckId(null); setCannotUpdateReason(''); }} zIndex="z-[60]">
        <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Güncellenemiyor</h3>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            Bu güncelleme kontrolünü "Güncellenemiyor" olarak işaretleyin. İsteğe bağlı olarak açıklama ekleyebilirsiniz.
          </p>
          <textarea
            placeholder="Güncellenememesinin sebebi (opsiyonel)"
            value={cannotUpdateReason}
            onChange={e => setCannotUpdateReason(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 mb-4"
          />
          <div className="flex justify-end gap-2">
            <button onClick={() => { setCannotUpdateCheckId(null); setCannotUpdateReason(''); }} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700">
              İptal
            </button>
            <button onClick={handleMarkCheckCannotUpdate} className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700">
              Güncellenemiyor Olarak İşaretle
            </button>
          </div>
        </div>
      </Modal>

      {/* Cannot Update Item Modal */}
      <Modal isOpen={cannotItemState !== null} onClose={() => { setCannotItemState(null); setCannotItemReason(''); }} zIndex="z-[60]">
        <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
              <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Kalem Güncellenemiyor</h3>
          </div>
          <textarea
            placeholder="Güncellenememesinin sebebi (opsiyonel)"
            value={cannotItemReason}
            onChange={e => setCannotItemReason(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 mb-4"
          />
          <div className="flex justify-end gap-2">
            <button onClick={() => { setCannotItemState(null); setCannotItemReason(''); }} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700">
              İptal
            </button>
            <button onClick={handleItemCannotUpdate} className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700">
              İşaretle
            </button>
          </div>
        </div>
      </Modal>

      {/* Edit Check Modal */}
      <Modal isOpen={editingCheck !== null} onClose={() => setEditingCheck(null)} zIndex="z-[60]">
        <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md p-6">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-4">Güncelleme Kontrolü Düzenle</h3>
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Başlık *"
              value={editCheckTitle}
              onChange={e => setEditCheckTitle(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
            <select
              value={editCheckAssignedTo}
              onChange={e => setEditCheckAssignedTo(parseInt(e.target.value))}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value={0}>Atama yapılmadı</option>
              {adminUsers.map(u => (
                <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
              ))}
            </select>
            <textarea
              placeholder="Not"
              value={editCheckNotes}
              onChange={e => setEditCheckNotes(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
            {editingCheck?.overall_status === 'cannot_update' && (
              <textarea
                placeholder="Güncellenememesinin sebebi"
                value={editCheckCannotReason}
                onChange={e => setEditCheckCannotReason(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 text-sm border border-red-300 dark:border-red-700 rounded-md focus:outline-none focus:ring-2 focus:ring-red-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setEditingCheck(null)} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700">
              İptal
            </button>
            <button onClick={handleEditCheckSave} className="px-4 py-2 text-sm bg-orange-500 text-white rounded-md hover:bg-orange-600">
              Kaydet
            </button>
          </div>
        </div>
      </Modal>

      {/* Edit Item Modal */}
      <Modal isOpen={editingItem !== null} onClose={() => setEditingItem(null)} zIndex="z-[60]">
        <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md p-6">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-4">Güncelleme Kalemi Düzenle</h3>
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Uygulama / Bileşen adı *"
              value={editItemApp}
              onChange={e => setEditItemApp(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
            <div className="grid grid-cols-2 gap-2">
              <input
                type="text"
                placeholder="Mevcut sürüm"
                value={editItemCurrent}
                onChange={e => setEditItemCurrent(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono"
              />
              <input
                type="text"
                placeholder="Hedef sürüm"
                value={editItemTarget}
                onChange={e => setEditItemTarget(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono"
              />
            </div>
            <textarea
              placeholder="Not"
              value={editItemNotes}
              onChange={e => setEditItemNotes(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setEditingItem(null)} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700">
              İptal
            </button>
            <button onClick={handleEditItemSave} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700">
              Kaydet
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ServerUpdateCheckModal;
